import React, { useState } from 'react';
import { LS_MY_TRADES_KEY } from './Table';
import {
  compareNumericString,
  compareTradeDateString,
} from '../../../helpers/tableSort';

interface ITableWidths {
  extender: number;
  date: number;
  pair: number;
  priceUSD: number;
  price: number;
  src: number;
  dest: number;
  type: number;
  valueUSD: number;
  exchange: number;
  tx: number;
  wallet: number;
  blocknumber: number;
  order: number;
}

// constants
const tableWidthsDefaultState: ITableWidths = {
  extender: 20,
  date: 150,
  pair: 180,
  price: 180,
  priceUSD: 180,
  src: 220,
  dest: 220,
  type: 120,
  valueUSD: 200,
  exchange: 120,
  tx: 90,
  wallet: 130,
  blocknumber: 140,
  order: 90,
};

interface IUseTableDataProps {
  swaps: any;
}

const useTableData = ({ swaps }: IUseTableDataProps) => {
  const tableWidthsLC = localStorage.getItem(LS_MY_TRADES_KEY);

  const [tableColumnsWidth] = useState(
    tableWidthsLC ? JSON.parse(tableWidthsLC) : tableWidthsDefaultState
  );

  // table memoized required fields
  const data = React.useMemo(() => swaps, [swaps]);
  const columns = React.useMemo(
    () => [
      {
        id: 'extender',
        Header: '',
        accessor: 'extender',
        disableSortBy: true,
        width:
          tableColumnsWidth['extender'] || tableWidthsDefaultState['extender'],
      },
      {
        id: 'blocknumber',
        Header: 'Block number',
        accessor: 'blocknumber',
        width:
          tableColumnsWidth['blocknumber'] ||
          tableWidthsDefaultState['blocknumber'],
        sortType: compareNumericString(),
      },
      {
        id: 'date',
        Header: 'Date',
        accessor: 'timestamp',
        width: tableColumnsWidth['date'] || tableWidthsDefaultState['date'],
        sortType: compareTradeDateString,
      },
      {
        id: 'pair',
        Header: 'Pair',
        accessor: 'ticker',
        disableSortBy: true,
        width: tableColumnsWidth['pair'] || tableWidthsDefaultState['pair'],
      },
      {
        id: 'priceUSD',
        Header: 'Price ($)',
        accessor: 'priceUSD',
        width:
          tableColumnsWidth['priceUSD'] || tableWidthsDefaultState['priceUSD'],
        sortType: compareNumericString(),
      },
      {
        id: 'price',
        Header: 'Price',
        accessor: 'price',
        width: tableColumnsWidth['price'] || tableWidthsDefaultState['price'],
        sortType: compareNumericString(),
      },
      {
        id: 'src',
        Header: 'Send',
        accessor: 'srcTokenAmount',
        width: tableColumnsWidth['src'] || tableWidthsDefaultState['src'],
        sortType: compareNumericString(),
      },
      {
        id: 'dest',
        Header: 'Receive',
        accessor: 'destTokenAmount',
        width: tableColumnsWidth['dest'] || tableWidthsDefaultState['dest'],
        sortType: compareNumericString(),
      },
      {
        id: 'type',
        Header: 'Type',
        accessor: 'swapType',
        width: tableColumnsWidth['type'] || tableWidthsDefaultState['type'],
        sortType: compareNumericString(),
      },
      {
        id: 'valueUSD',
        Header: 'Tx Value ($)',
        accessor: 'valueUSD',
        width:
          tableColumnsWidth['valueUSD'] || tableWidthsDefaultState['valueUSD'],
        sortType: compareNumericString(),
      },
      {
        id: 'exchange',
        Header: 'Exchange',
        accessor: 'exchange',
        width:
          tableColumnsWidth['exchange'] || tableWidthsDefaultState['exchange'],
      },
      {
        id: 'tx',
        Header: 'Tx Hash',
        accessor: 'tx',
        disableSortBy: true,
        width: tableColumnsWidth['tx'] || tableWidthsDefaultState['tx'],
      },
      {
        id: 'wallet',
        Header: 'Address',
        accessor: 'wallet',
        disableSortBy: true,
        width: tableColumnsWidth['wallet'] || tableWidthsDefaultState['wallet'],
      },
      {
        id: 'order',
        Header: 'Log Order',
        accessor: 'order',
        width: tableColumnsWidth['order'] || tableWidthsDefaultState['order'],
      },
    ],
    []
  );

  const subColumns = React.useMemo(
    () => [
      {
        header: 'Pair',
        accessor: 'ticker',
      },
      {
        header: 'Price ($)',
        accessor: 'priceUSD',
      },
      {
        header: 'Price',
        accessor: 'price',
      },
      {
        header: 'Send',
        accessor: 'srcTokenAmount',
      },
      {
        header: 'Receive',
        accessor: 'destTokenAmount',
      },
      {
        header: 'Type',
        accessor: 'swapType',
      },
      {
        header: 'Side',
        accessor: 'isBuy',
      },
      {
        header: 'Tx Value ($)',
        accessor: 'valueUSD',
      },
      {
        header: 'Exchange',
        accessor: 'exchange',
      },
    ],
    []
  );

  return { columns, data, tableColumnsWidth, subColumns };
};

export { useTableData };
